import { createContext, useReducer, useState } from "react";
import PropTypes from "prop-types";
import axios from "../utils/axios";
import { isValidToken, setSession } from "../utils/jwt";
import { getDeviceId } from "../utils/uuidv4";

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};

const handlers = {
  INITIALIZE: (state, action) => ({
    ...state,
    isAuthenticated: action.payload.isAuthenticated,
    isInitialized: true,
    user: action.payload.user,
  }),
  LOGIN: (state, action) => ({
    ...state,
    isAuthenticated: true,
    user: action.payload.user,
  }),
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null,
  }),
};

const reducer = (state, action) =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

const AuthContext = createContext({
  ...initialState,
  method: "jwt",
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  refresh: () => Promise.resolve(),
  initializeAuth: () => Promise.resolve(), 
});

function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [isInitializing, setIsInitializing] = useState(false);

  const initializeAuth = async () => {
    if (isInitializing) return; 
    setIsInitializing(true);

    try {
      const accessToken = localStorage.getItem("accessToken");
      
      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken);
        const response = await axios.get("/api/desk/user");
        dispatch({
          type: "INITIALIZE",
          payload: { isAuthenticated: true, user: response.data },
        });
      } else {
        dispatch({
          type: "INITIALIZE",
          payload: { isAuthenticated: false, user: null },
        });
      }
    } catch (err) {
      console.error("Auth initialization error:", err);
      setSession(null);
      dispatch({
        type: "INITIALIZE",
        payload: { isAuthenticated: false, user: null },
      });
    } finally {
      setIsInitializing(false);
    }
  };

  const login = async (username, password) => {
    const response = await axios.post("/api/user/auth/login", { username, password });
    const { accessToken, user } = response.data;
    const deviceId = getDeviceId();

    if (user?.imei !== deviceId) return false;

    setSession(accessToken);
    dispatch({ type: "LOGIN", payload: { user } });
    return true;
  };

  const logout = async () => {
    try {
      await axios.post("/api/user/auth/logout", {});
    } catch (e) {
      console.error("Logout error:", e);
    } finally {
      setSession(null);
      dispatch({ type: "LOGOUT" });
    }
  };

  const refresh = async () => {
    const response = await axios.get("/api/desk/user");
    dispatch({
      type: "INITIALIZE",
      payload: { isAuthenticated: true, user: response.data },
    });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: "jwt",
        login,
        logout,
        refresh,
        initializeAuth, 
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };